import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { useParams, Link, useLocation } from "react-router-dom";
import { format } from "date-fns";
import MDSpinner from "react-md-spinner";
import { toast, ToastContainer } from "react-toastify";
import {
  FaMobileAlt,
  FaGasPump,
  FaClipboardList,
  FaSeedling,
  FaMapMarkerAlt,
  FaList,
  FaWrench,
  FaUser,
  FaTractor,
  FaCalendarAlt,
  FaMapMarkedAlt,
  FaBell,
  FaRulerCombined,
  FaExclamationTriangle,
  FaBullseye,
} from "react-icons/fa";

import Title from "../../components/Title";
import { usePendingActions } from "../../contexts/PendingActionsContext";
import { countOverdueMaintenances } from "../../hooks/useMaintenanceOverdueCount";
import {
  fetchFieldAppFilters,
  fetchFieldAppSummary,
  fetchFuelingRecords,
  fetchSyncRecords,
  fetchSyncRecordDetail,
  fetchScheduledMaintenances,
  fetchOperatorAlerts,
  fetchTelemetrySchedule,
  updateFieldMaintenanceRecord,
  updateScheduledMaintenance,
  createScheduledMaintenance,
  completeScheduledMaintenance,
} from "./fieldAppApi";
import FieldAppAddMaintenanceModal from "./FieldAppAddMaintenanceModal";
import FieldAppCompleteMaintenanceModal from "./FieldAppCompleteMaintenanceModal";
import FieldAppMachineMaintenancesModal from "./FieldAppMachineMaintenancesModal";
import FieldAppRecordDetail, { fuelRecordToDetail } from "./FieldAppRecordDetail";
import FieldAppTrackingPanel from "./FieldAppTrackingPanel";
import FieldAppOperatorAlertsPanel from "./FieldAppOperatorAlertsPanel";
import FieldAppFuelControlPanel from "./FieldAppFuelControlPanel";
import FieldAppFuelSupplySpreadsheet from "./FieldAppFuelSupplySpreadsheet";
import {
  enrichFuelingRecords,
  FUEL_INTERVAL_MODES,
  FUEL_CONTROL_EXAMPLE_RECORDS,
  summarizeIntervalsByMachine,
} from "./fuelSupplyMetrics";
import "./FieldAppFuelControlMatrix.css";
import "../Operation/OperationFuelSupplyPanel.css";
import FieldAppInputsPanel from "./FieldAppInputsPanel";
import FieldAppCalibrationPanel from "./FieldAppCalibrationPanel";
import FieldAppCalibrationWorkSection from "./FieldAppCalibrationWorkSection";
import FarmMapSyncLeaveModal from "../FarmMap/FarmMapSyncLeaveModal";
import { useFarmMapPublishGuard } from "../FarmMap/useFarmMapPublishGuard";
import FieldAppMaintenancePanel from "./FieldAppMaintenancePanel";
import FieldAppMaintenanceEditableHours from "./FieldAppMaintenanceEditableHours";
import {
  compareMaintenanceProximity,
  computeMaintenanceRemaining,
  equipmentKeyFromScheduleRow,
  formatMaintenanceRemaining,
} from "./maintenanceRemaining";
import FieldAppMachinesPanel from "./FieldAppMachinesPanel";
import FieldAppPendingActionsPanel from "./FieldAppPendingActionsPanel";
import FieldAppTelemetrySchedulePanel from "./FieldAppTelemetrySchedulePanel";
import FieldAppPointsOfInterestPanel from "./FieldAppPointsOfInterestPanel";
import { groupInputRefillRecords } from "./fieldAppInputRefillUtils";
import { eventLabel, recordTypeLabel } from "./fieldAppDetailLabels";
import {
  PERIOD_PRESETS,
  allPeriodDaysSelected,
  buildCustomPeriod,
  buildLastMonthPeriod,
  buildLastWeekPeriod,
  buildMonthToDatePeriod,
  buildPeriodDayButtons,
  buildServiceMonthPeriod,
  dayKeysForPeriod,
  defaultVisibleDayKeys,
  formatSelectedDaysLabel,
  periodEndsToday,
  presetLabel,
  refreshLivePeriodEnd,
  resolveEffectiveSelection,
  todayIso,
  buildSingleOperatorSelection,
} from "./fieldAppPeriodUtils";
import {
  readTrackingTimezoneMode,
} from "./fieldAppTimezoneDisplay";
import {
  applyChartDayClick,
} from "./FieldAppActivityTimeline";

import "./FieldAppHome.css";

const TABS = [
  { id: "summary", label: "Resumo", icon: FaMobileAlt },
  { id: "tracking", label: "Rastreamento", icon: FaMapMarkedAlt },
  { id: "operator-alerts", label: "Alertas ao app", icon: FaBell },
  { id: "fueling", label: "Combustível", icon: FaGasPump },
  {
    id: "machines",
    label: "Máquinas",
    icon: FaTractor,
    recordType: "operational_event",
    eventType: "ASSUMIR_MAQUINA",
  },
  { id: "events", label: "Eventos", icon: FaClipboardList, recordType: "operational_event" },
  {
    id: "points-of-interest",
    label: "Pontos de interesse",
    icon: FaBullseye,
    recordType: "operational_event",
    eventType: "PONTO_INTERESSE",
  },
  { id: "inputs", label: "Insumos", icon: FaSeedling },
  { id: "telemetry", label: "Telemetria", icon: FaMapMarkerAlt, recordType: "telemetry_sample" },
  { id: "maintenance", label: "Manutenções", icon: FaWrench },
  {
    id: "calibration",
    label: "Calibragem",
    icon: FaRulerCombined,
    recordType: "operational_event",
    eventType: "CHECK_CALIBRAGEM",
  },
  { id: "all", label: "Todos", icon: FaList },
];

const APP_MIRROR_SECTIONS = [
  {
    tabId: "tracking",
    title: "Telemetria e mapa",
    text: "Posição do operador, velocidade e mapa da fazenda — como na Home do app com telemetria ativa.",
  },
  {
    tabId: "operator-alerts",
    title: "Alertas ao app",
    text: "Envie aviso para o tablet. O operador confirma a leitura com OK.",
  },
  {
    tabId: "inputs",
    title: "Insumos a cada abastecimento",
    text: "Dosagem de adubo, semente ou defensivo a cada parada na base.",
  },
  {
    tabId: "fueling",
    title: "Controle de combustível",
    text: "Abastecimentos registrados no app: litros, horímetro e foto do painel.",
  },
  {
    tabId: "machines",
    title: "Máquinas operadas",
    text:
      "Quais máquinas o operador assumiu no período — com atalho para o monitoramento em Operações.",
  },
  {
    tabId: "maintenance",
    title: "Manutenções",
    text:
      "Histórico, próximas revisões, horas restantes, óleos e filtros — com alertas de vencimento.",
  },
  {
    tabId: "calibration",
    title: "Check de calibragem",
    text:
      "Produto, taxa-alvo e quantidade por abastecimento — vinculados ao rastreamento e à ordem de serviço.",
  },
  {
    tabId: "points-of-interest",
    title: "Pontos de interesse",
    text:
      "Toco, pedra, valeta, buraco ou texto livre marcados no campo — e envio desses pontos ao app de um operador.",
  },
];

const TABS_WITHOUT_SELECTION = new Set([
  "operator-alerts",
  "maintenance",
  "pending-actions",
]);

const SELECTION_STORAGE_PREFIX = "field-app-selection-v3:";

function datePart(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function defaultDraftPeriod() {
  const period = buildLastWeekPeriod();
  return {
    draftPeriodPreset: PERIOD_PRESETS.LAST_WEEK,
    draftDateFrom: datePart(period.dateFrom),
    draftDateTo: datePart(period.dateTo),
  };
}

function resolveDraftPeriod(draftPeriodPreset, draftDateFrom, draftDateTo) {
  switch (draftPeriodPreset) {
    case PERIOD_PRESETS.LAST_MONTH:
      return buildLastMonthPeriod();
    case PERIOD_PRESETS.MONTH_TO_DATE:
      return buildMonthToDatePeriod();
    case PERIOD_PRESETS.CUSTOM:
      return buildCustomPeriod(draftDateFrom, draftDateTo);
    case PERIOD_PRESETS.LAST_WEEK:
    default:
      return buildLastWeekPeriod();
  }
}

function hydrateStoredSelection(selection) {
  if (!selection) return null;
  if (selection.periodPreset) {
    return refreshLivePeriodEnd(selection);
  }
  if (selection.periodAutoToday === true) {
    return {
      ...selection,
      ...buildLastWeekPeriod(),
      periodAutoToday: false,
    };
  }
  return {
    ...selection,
    periodPreset: PERIOD_PRESETS.CUSTOM,
    periodAutoToday: false,
  };
}

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return format(new Date(value), "dd/MM/yyyy HH:mm");
  } catch (error) {
    return String(value);
  }
}

function formatDateLabel(value) {
  if (!value) return "—";
  try {
    const [year, month, day] = String(value).slice(0, 10).split("-");
    return `${day}/${month}/${year}`;
  } catch (error) {
    return String(value);
  }
}

function payloadPreview(payload) {
  if (!payload || typeof payload !== "object") return "—";
  const keys = [
    "nome_trabalho",
    "tipo_evento",
    "id_maquina",
    "valor_principal",
    "horimetro_maquina",
    "hectares_painel_inicial",
    "hectares_painel",
    "observacoes",
    "texto",
  ];
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (payload[key]) return String(payload[key]);
  }
  const serialized = JSON.stringify(payload);
  return serialized.length > 80 ? `${serialized.slice(0, 80)}…` : serialized;
}

function formatHourMeter(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("pt-BR", { maximumFractionDigits: 1 });
}

function normalizeSelection(raw) {
  if (!raw) return null;
  // migração do formato antigo (um operador / uma máquina)
  if (raw.operatorId && !raw.operatorIds) {
    return {
      operatorIds: [raw.operatorId],
      operatorNames: raw.operatorName ? [raw.operatorName] : [],
      allMachines: false,
      equipmentIds: raw.equipmentId ? [raw.equipmentId] : [],
      machineIdents: raw.machineIdent ? [raw.machineIdent] : [],
      dateFrom: raw.dateFrom || buildLastWeekPeriod().dateFrom,
      dateTo: raw.dateTo || buildLastWeekPeriod().dateTo,
      periodAutoToday: false,
      periodPreset: raw.periodPreset || PERIOD_PRESETS.CUSTOM,
    };
  }
  if (!Array.isArray(raw.operatorIds) || raw.operatorIds.length === 0) {
    return null;
  }
  if (!raw.allMachines && (!raw.equipmentIds || raw.equipmentIds.length === 0)) {
    return null;
  }
  if (!raw.dateFrom || !raw.dateTo) {
    return null;
  }
  const allMachines = Boolean(raw.allMachines);
  return {
    operatorIds: raw.operatorIds.map(Number),
    operatorNames: raw.operatorNames || [],
    allMachines,
    equipmentIds: allMachines ? [] : (raw.equipmentIds || []).map(Number),
    machineIdents: allMachines ? [] : raw.machineIdents || [],
    dateFrom: raw.dateFrom,
    dateTo: raw.dateTo,
    periodAutoToday: raw.periodAutoToday === true,
    periodPreset: raw.periodPreset || PERIOD_PRESETS.CUSTOM,
  };
}

function readStoredSelection(farmId) {
  try {
    const raw = localStorage.getItem(`${SELECTION_STORAGE_PREFIX}${farmId}`);
    if (!raw) return null;
    return normalizeSelection(JSON.parse(raw));
  } catch (error) {
    return null;
  }
}

function writeStoredSelection(farmId, selection) {
  try {
    localStorage.setItem(
      `${SELECTION_STORAGE_PREFIX}${farmId}`,
      JSON.stringify(selection)
    );
  } catch (error) {
    // ignore quota / private mode
  }
}

function toggleId(list, id) {
  const value = Number(id);
  if (list.includes(value)) {
    return list.filter((item) => item !== value);
  }
  return [...list, value];
}

const FIELD_APP_TAB_IDS = new Set([
  ...TABS.map((tab) => tab.id),
  "pending-actions",
]);

function resolveFieldAppTab(search) {
  const tab = new URLSearchParams(search).get("tab");
  return tab && FIELD_APP_TAB_IDS.has(tab) ? tab : "summary";
}

function FieldAppHome({ t }) {
  const params = useParams();
  const farmId = params.farm_id;
  const location = useLocation();
  const { refreshPendingActions } = usePendingActions();

  const [activeTab, setActiveTab] = useState(() =>
    resolveFieldAppTab(location.search)
  );
  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [filtersLoadError, setFiltersLoadError] = useState("");
  const [machinesLoading, setMachinesLoading] = useState(false);
  const [operators, setOperators] = useState([]);
  const [machines, setMachines] = useState([]);
  const initialDraftPeriod = defaultDraftPeriod();
  const [draftOperatorIds, setDraftOperatorIds] = useState([]);
  const [draftAllMachines, setDraftAllMachines] = useState(true);
  const [draftEquipmentIds, setDraftEquipmentIds] = useState([]);
  const [draftPeriodPreset, setDraftPeriodPreset] = useState(
    initialDraftPeriod.draftPeriodPreset
  );
  const [draftDateFrom, setDraftDateFrom] = useState(
    initialDraftPeriod.draftDateFrom
  );
  const [draftDateTo, setDraftDateTo] = useState(initialDraftPeriod.draftDateTo);
  const [selection, setSelection] = useState(null);
  const [selectionStep, setSelectionStep] = useState("operators");
  const [selectedDayKeys, setSelectedDayKeys] = useState([]);
  const [summary, setSummary] = useState(null);
  const [fuelingRows, setFuelingRows] = useState([]);
  const [syncRows, setSyncRows] = useState([]);
  const [scheduleRows, setScheduleRows] = useState([]);
  const [maintenanceFieldRecords, setMaintenanceFieldRecords] = useState([]);
  const [completeMaintenanceRow, setCompleteMaintenanceRow] = useState(null);
  const [completingMaintenance, setCompletingMaintenance] = useState(false);
  const [machineMaintenancesKey, setMachineMaintenancesKey] = useState(null);
  const [addMaintenanceOpen, setAddMaintenanceOpen] = useState(false);
  const [addMaintenanceEquipmentId, setAddMaintenanceEquipmentId] = useState(
    null
  );
  const [addingMaintenance, setAddingMaintenance] = useState(false);
  const [inputWorkGroups, setInputWorkGroups] = useState([]);
  const [alertCount, setAlertCount] = useState(0);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [trackingRefreshToken, setTrackingRefreshToken] = useState(0);
  const [trackingOperatorId, setTrackingOperatorId] = useState(null);
  const [focusSessionUuid, setFocusSessionUuid] = useState(null);
  const [trackingViewMode, setTrackingViewMode] = useState("map");
  const [focusGraphPeriod, setFocusGraphPeriod] = useState(null);
  const [telemetryScheduleRows, setTelemetryScheduleRows] = useState([]);
  const [telemetryScheduleLoading, setTelemetryScheduleLoading] = useState(false);
  const calibrationPublishRef = useRef(async () => {});

  const calibrationPublishGuard = useFarmMapPublishGuard({
    onPublish: () => calibrationPublishRef.current(),
  });

  const handleCalibrationUnpublishedStatus = useCallback(
    (hasUnpublished) => {
      if (hasUnpublished) {
        calibrationPublishGuard.markUnpublished();
      } else {
        calibrationPublishGuard.clearUnpublished();
      }
    },
    [calibrationPublishGuard]
  );

  const effectiveSelection = useMemo(
    () => resolveEffectiveSelection(selection, selectedDayKeys),
    [selection, selectedDayKeys]
  );

  const showCombinedTrails = Boolean(
    trackingViewMode === "map" &&
      selection &&
      selection.operatorIds &&
      selection.operatorIds.length > 1
  );

  const trackingSelection = useMemo(() => {
    if (!selection) return null;
    if (showCombinedTrails) return selection;
    if (selection.operatorIds.length > 1) {
      if (trackingOperatorId == null) return null;
      return buildSingleOperatorSelection(selection, trackingOperatorId);
    }
    return selection;
  }, [selection, showCombinedTrails, trackingOperatorId]);

  const trackingChartSelection = useMemo(() => {
    if (!effectiveSelection) return null;
    if (showCombinedTrails) return effectiveSelection;
    if (effectiveSelection.operatorIds.length > 1) {
      if (trackingOperatorId == null) return null;
      return buildSingleOperatorSelection(
        effectiveSelection,
        trackingOperatorId
      );
    }
    return effectiveSelection;
  }, [effectiveSelection, showCombinedTrails, trackingOperatorId]);

  const trackingRequiresOperatorChoice = Boolean(
    !showCombinedTrails &&
      selection &&
      selection.operatorIds &&
      selection.operatorIds.length > 1 &&
      trackingOperatorId == null
  );

  useEffect(() => {
    if (!selection || !Array.isArray(selection.operatorIds)) {
      setTrackingOperatorId(null);
      return;
    }
    if (selection.operatorIds.length === 1) {
      setTrackingOperatorId(Number(selection.operatorIds[0]));
      setTrackingViewMode("detail");
      return;
    }
    setTrackingOperatorId((current) => {
      if (
        current != null &&
        selection.operatorIds.some((id) => Number(id) === Number(current))
      ) {
        return current;
      }
      return null;
    });
  }, [selection]);

  const enrichedFueling = useMemo(
    () =>
      enrichFuelingRecords(fuelingRows, {
        totalAreaHa: 0,
        totalHours: 0,
        intervalMode: FUEL_INTERVAL_MODES.FILL_CLOSING,
      }),
    [fuelingRows]
  );

  const fuelMachineSummaries = useMemo(
    () =>
      summarizeIntervalsByMachine(fuelingRows, {
        totalAreaHa: 0,
        totalHours: 0,
        intervalMode: FUEL_INTERVAL_MODES.FILL_CLOSING,
      }),
    [fuelingRows]
  );

  const periodDayKeys = useMemo(
    () =>
      selection
        ? dayKeysForPeriod(selection.dateFrom, selection.dateTo)
        : [],
    [selection]
  );

  const allBannerDaysSelected = useMemo(
    () => allPeriodDaysSelected(periodDayKeys, selectedDayKeys),
    [periodDayKeys, selectedDayKeys]
  );

  const periodDayButtons = useMemo(
    () =>
      selection
        ? buildPeriodDayButtons(selection.dateFrom, selection.dateTo)
        : [],
    [selection]
  );

  const selectionReady = Boolean(
    effectiveSelection &&
      effectiveSelection.dateFrom &&
      effectiveSelection.dateTo &&
      effectiveSelection.operatorIds &&
      effectiveSelection.operatorIds.length > 0 &&
      (effectiveSelection.allMachines ||
        (effectiveSelection.equipmentIds &&
          effectiveSelection.equipmentIds.length > 0))
  );

  const requiresSelection =
    !TABS_WITHOUT_SELECTION.has(activeTab) && !selectionReady;

  const selectedOperatorFullLabel = useMemo(() => {
    if (!selection) return "";
    return selection.operatorIds
      .map((id) => {
        const found = operators.find((item) => Number(item.id) === Number(id));
        return found ? found.name : String(id);
      })
      .join(", ");
  }, [operators, selection]);

  const selectedOperatorLabel = useMemo(() => {
    if (!selection) return "";
    const names = selection.operatorIds.map((id) => {
      const found = operators.find((item) => Number(item.id) === Number(id));
      return found ? found.name : String(id);
    });
    if (names.length <= 2) return names.join(", ");
    return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
  }, [operators, selection]);

  const selectedMachineLabel = useMemo(() => {
    if (!selection) return "";
    if (selection.allMachines) return t("Máquinas operadas no período");
    const idents = selection.machineIdents || [];
    if (idents.length <= 2) return idents.join(", ");
    return `${idents.slice(0, 2).join(", ")} +${idents.length - 2}`;
  }, [selection, t]);

  const selectedPeriodLabel = useMemo(() => {
    if (!selection) return "";
    if (!allBannerDaysSelected && selectedDayKeys.length) {
      return formatSelectedDaysLabel(selectedDayKeys);
    }
    const preset = selection.periodPreset || PERIOD_PRESETS.CUSTOM;
    if (preset !== PERIOD_PRESETS.CUSTOM) {
      return presetLabel(preset, t);
    }
    const fromLabel = formatDateLabel(selection.dateFrom);
    const toLabel = formatDateLabel(selection.dateTo);
    if (datePart(selection.dateFrom) === datePart(selection.dateTo)) {
      return fromLabel;
    }
    return `${fromLabel} — ${toLabel}`;
  }, [allBannerDaysSelected, selectedDayKeys, selection, t]);

  const draftOperatorsReady = draftOperatorIds.length > 0;
  const draftPeriodReady =
    draftPeriodPreset !== PERIOD_PRESETS.CUSTOM ||
    (Boolean(draftDateFrom) &&
      Boolean(draftDateTo) &&
      draftDateFrom <= draftDateTo);
  const draftCanContinue = draftOperatorsReady && draftPeriodReady;

  useEffect(() => {
    setActiveTab(resolveFieldAppTab(location.search));
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;

    async function loadFilters() {
      setFiltersLoading(true);
      setFiltersLoadError("");
      try {
        const data = await fetchFieldAppFilters(farmId);
        if (cancelled) return;
        setOperators(data.operators || []);
        setMachines(data.machines || []);

        const stored = readStoredSelection(farmId);
        if (stored) {
          const operatorOk =
            !stored.operatorIds ||
            !stored.operatorIds.length ||
            stored.operatorIds.every((id) =>
              (data.operators || []).some((item) => Number(item.id) === Number(id))
            );
          if (operatorOk) {
            const refreshed = hydrateStoredSelection(stored);
            setSelection(refreshed);
            const storedPeriodDayKeys = dayKeysForPeriod(
              refreshed.dateFrom,
              refreshed.dateTo
            );
            setSelectedDayKeys(defaultVisibleDayKeys(storedPeriodDayKeys));
            setDraftOperatorIds(refreshed.operatorIds || []);
            setDraftAllMachines(Boolean(refreshed.allMachines));
            setDraftEquipmentIds(refreshed.equipmentIds || []);
            setDraftPeriodPreset(
              refreshed.periodPreset || PERIOD_PRESETS.CUSTOM
            );
            setDraftDateFrom(datePart(refreshed.dateFrom));
            setDraftDateTo(datePart(refreshed.dateTo));
            if (refreshed !== stored) {
              writeStoredSelection(farmId, refreshed);
            }
          } else {
            setSelection(null);
          }
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          const status = error.response && error.response.status;
          if (status === 401 || status === 403) {
            setFiltersLoadError(
              t(
                "Sua sessão expirou. Faça login novamente para carregar operadores e máquinas."
              )
            );
          } else {
            setFiltersLoadError(
              t(
                "Falha ao carregar operadores e máquinas. Verifique se a API está rodando."
              )
            );
          }
          toast.error(t("Falha ao carregar operadores e máquinas"), {
            position: "bottom-right",
          });
        }
      } finally {
        if (!cancelled) setFiltersLoading(false);
      }
    }

    loadFilters();
    return () => {
      cancelled = true;
    };
  }, [farmId, t]);

  useEffect(() => {
    if (!selection || !farmId) return;
    const refreshed = refreshLivePeriodEnd(selection);
    if (refreshed === selection) return;
    const previousDays = dayKeysForPeriod(selection.dateFrom, selection.dateTo);
    const nextDays = dayKeysForPeriod(refreshed.dateFrom, refreshed.dateTo);
    writeStoredSelection(farmId, refreshed);
    setSelection(refreshed);
    setDraftDateFrom(datePart(refreshed.dateFrom));
    setDraftDateTo(datePart(refreshed.dateTo));
    setDraftPeriodPreset(refreshed.periodPreset || PERIOD_PRESETS.CUSTOM);
    setSelectedDayKeys((current) => {
      if (
        !current ||
        !current.length ||
        allPeriodDaysSelected(previousDays, current)
      ) {
        return defaultVisibleDayKeys(nextDays);
      }
      const kept = current.filter((key) => nextDays.indexOf(key) !== -1);
      return kept.length ? kept : defaultVisibleDayKeys(nextDays);
    });
  }, [farmId, selection]);

  useEffect(() => {
    let cancelled = false;

    async function loadMachinesForOperators() {
      setMachinesLoading(true);
      try {
        const data = await fetchFieldAppFilters(
          farmId,
          draftOperatorIds.length ? draftOperatorIds : []
        );
        if (cancelled) return;
        const nextMachines = data.machines || [];
        setMachines(nextMachines);
        setDraftEquipmentIds((current) =>
          current.filter((id) =>
            nextMachines.some((machine) => Number(machine.id) === Number(id))
          )
        );
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          toast.error(t("Falha ao carregar máquinas desses operadores"));
        }
      } finally {
        if (!cancelled) setMachinesLoading(false);
      }
    }

    loadMachinesForOperators();
    return () => {
      cancelled = true;
    };
  }, [draftOperatorIds, farmId, t]);

  const loadSummary = useCallback(async () => {
    if (!effectiveSelection) return;
    const data = await fetchFieldAppSummary(farmId, effectiveSelection);
    setSummary(data);
  }, [farmId, effectiveSelection]);

  const loadFueling = useCallback(async () => {
    if (!effectiveSelection) return;
    const data = await fetchFuelingRecords(farmId, {}, effectiveSelection);
    setFuelingRows(data.results || []);
  }, [farmId, effectiveSelection]);

  const loadScheduledMaintenances = useCallback(async () => {
    const data = await fetchScheduledMaintenances(farmId, { limit: 500 }, {});
    setScheduleRows(data.results || []);
    setMaintenanceFieldRecords(data.fieldRecords || []);
    refreshPendingActions({ background: true });
  }, [farmId, refreshPendingActions]);

  const handleMaintenanceFieldRecordSave = useCallback(
    async ({ recordUuid, value }) => {
      const updated = await updateFieldMaintenanceRecord(farmId, recordUuid, {
        targetHourMeter: value,
      });
      setMaintenanceFieldRecords((rows) =>
        rows.map((row) =>
          row.uuid === recordUuid
            ? {
                ...row,
                ...updated,
                targetHourMeter:
                  updated.targetHourMeter != null
                    ? updated.targetHourMeter
                    : value,
                targetHourMeterDisplay:
                  updated.targetHourMeterDisplay != null
                    ? updated.targetHourMeterDisplay
                    : value,
              }
            : row
        )
      );
      await loadScheduledMaintenances();
    },
    [farmId, loadScheduledMaintenances]
  );

  const handleCompleteMaintenance = useCallback(
    async (payload) => {
      const maintenanceId =
        completeMaintenanceRow &&
        (completeMaintenanceRow.id ||
          completeMaintenanceRow.scheduledMaintenanceId);
      if (!maintenanceId) return;
      setCompletingMaintenance(true);
      try {
        await completeScheduledMaintenance(farmId, maintenanceId, payload);
        toast.success(t("Manutenção concluída."));
        setCompleteMaintenanceRow(null);
        await loadScheduledMaintenances();
      } catch (error) {
        console.error(error);
        const data = error && error.response && error.response.data;
        const detail =
          (data && (data.hourMeter || data.odometer || data.completedDate)) ||
          t("Não foi possível concluir a manutenção.");
        toast.error(detail);
      } finally {
        setCompletingMaintenance(false);
      }
    },
    [completeMaintenanceRow, farmId, loadScheduledMaintenances, t]
  );

  const handleScheduledMaintenanceSave = useCallback(
    async ({ maintenanceId, value }) => {
      const updated = await updateScheduledMaintenance(farmId, maintenanceId, {
        targetHourMeter: value,
      });
      setScheduleRows((rows) =>
        rows.map((row) =>
          row.id === maintenanceId
            ? {
                ...row,
                ...updated,
                targetHourMeterDisplay:
                  updated.targetHourMeterDisplay != null
                    ? updated.targetHourMeterDisplay
                    : value,
              }
            : row
        )
      );
      await loadScheduledMaintenances();
    },
    [farmId, loadScheduledMaintenances]
  );

  const openAddMaintenance = useCallback((equipmentId = null) => {
    setAddMaintenanceEquipmentId(
      equipmentId != null && equipmentId !== "" ? equipmentId : null
    );
    setAddMaintenanceOpen(true);
  }, []);

  const handleAddMaintenance = useCallback(
    async (payload) => {
      setAddingMaintenance(true);
      try {
        const result = await createScheduledMaintenance(farmId, payload);
        const count =
          (result && result.count) ||
          (payload && payload.names && payload.names.length) ||
          1;
        toast.success(
          count > 1
            ? t("{{count}} manutenções salvas.", { count })
            : t("Manutenção salva.")
        );
        setAddMaintenanceOpen(false);
        setAddMaintenanceEquipmentId(null);
        await loadScheduledMaintenances();
      } catch (error) {
        console.error(error);
        const data = error && error.response && error.response.data;
        const detail =
          (data && (data.error || data.detail || data.name)) ||
          t("Não foi possível adicionar a manutenção.");
        toast.error(typeof detail === "string" ? detail : t("Não foi possível adicionar a manutenção."));
      } finally {
        setAddingMaintenance(false);
      }
    },
    [farmId, loadScheduledMaintenances, t]
  );

  const loadInputs = useCallback(async () => {
    if (!effectiveSelection) return;
    const [worksPage, refillsPage] = await Promise.all([
      fetchSyncRecords(
        farmId,
        { record_type: "input_refill_work", limit: 100 },
        effectiveSelection
      ),
      fetchSyncRecords(
        farmId,
        { record_type: "input_refill_record", limit: 200 },
        effectiveSelection
      ),
    ]);
    setInputWorkGroups(
      groupInputRefillRecords(worksPage.results || [], refillsPage.results || [])
    );
  }, [farmId, effectiveSelection]);

  const loadAlertCount = useCallback(async () => {
    try {
      const data = await fetchOperatorAlerts(farmId);
      setAlertCount((data && data.count) || 0);
    } catch (error) {
      setAlertCount(0);
    }
  }, [farmId]);

  const loadTelemetrySchedule = useCallback(async () => {
    if (!effectiveSelection) return;
    setTelemetryScheduleLoading(true);
    try {
      const data = await fetchTelemetrySchedule(farmId, effectiveSelection);
      setTelemetryScheduleRows(data.days || []);
    } catch (error) {
      console.error(error);
      setTelemetryScheduleRows([]);
      const status = error && error.response && error.response.status;
      if (status === 401 || status === 403) {
        toast.error(
          t("Sessão expirada. Entre novamente para carregar a telemetria.")
        );
      } else {
        toast.error(t("Não foi possível carregar a telemetria."));
      }
    } finally {
      setTelemetryScheduleLoading(false);
    }
  }, [farmId, effectiveSelection, t]);

  const loadSync = useCallback(
    async (tabConfig) => {
      if (!effectiveSelection) return;
      const query = {};
      if (tabConfig.recordType) {
        query.record_type = tabConfig.recordType;
      }
      if (tabConfig.eventType) {
        query.event_type = tabConfig.eventType;
      }
      if (tabConfig.recordTypes) {
        const results = await Promise.all(
          tabConfig.recordTypes.map((recordType) =>
            fetchSyncRecords(
              farmId,
              { record_type: recordType, limit: 50 },
              effectiveSelection
            )
          )
        );
        const merged = results
          .flatMap((page) => page.results || [])
          .sort(
            (a, b) =>
              new Date(b.received_at).getTime() -
              new Date(a.received_at).getTime()
          );
        setSyncRows(merged);
        return;
      }
      const data = await fetchSyncRecords(farmId, query, effectiveSelection);
      setSyncRows(data.results || []);
    },
    [farmId, effectiveSelection]
  );

  const refresh = useCallback(async () => {
    if (!effectiveSelection) return;
    const isTrackingTab = activeTab === "tracking";
    if (!isTrackingTab) {
      setLoading(true);
    }
    try {
      await loadSummary();
      if (activeTab === "fueling") {
        await loadFueling();
      } else if (activeTab === "maintenance") {
        await loadScheduledMaintenances();
      } else if (activeTab === "inputs") {
        await loadInputs();
      } else if (activeTab === "telemetry") {
        await loadTelemetrySchedule();
      } else if (activeTab !== "summary" && activeTab !== "operator-alerts" && !isTrackingTab) {
        const tab = TABS.find((item) => item.id === activeTab) || TABS[0];
        await loadSync(tab);
      }
      if (activeTab === "summary") {
        await loadAlertCount();
      }
    } catch (error) {
      console.error(error);
      toast.error(t("Falha ao carregar dados do app de campo"));
    } finally {
      if (!isTrackingTab) {
        setLoading(false);
      }
    }
  }, [
    activeTab,
    loadFueling,
    loadInputs,
    loadAlertCount,
    loadScheduledMaintenances,
    loadSummary,
    loadSync,
    loadTelemetrySchedule,
    effectiveSelection,
    t,
  ]);

  const handleRefresh = useCallback(async () => {
    if (activeTab === "maintenance") {
      setLoading(true);
      try {
        await loadScheduledMaintenances();
      } catch (error) {
        console.error(error);
        toast.error(t("Falha ao carregar manutenções"));
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!effectiveSelection) return;
    if (activeTab === "tracking") {
      setTrackingRefreshToken((value) => value + 1);
      try {
        await loadSummary();
      } catch (error) {
        console.error(error);
        toast.error(t("Falha ao carregar dados do app de campo"));
      }
      return;
    }
    await refresh();
  }, [activeTab, effectiveSelection, loadScheduledMaintenances, loadSummary, refresh, t]);

  useEffect(() => {
    if (!selectionReady || activeTab === "maintenance") return;
    refresh();
  }, [activeTab, refresh, selectionReady]);

  useEffect(() => {
    if (activeTab !== "maintenance") return;
    setLoading(true);
    loadScheduledMaintenances()
      .catch((error) => {
        console.error(error);
        toast.error(t("Falha ao carregar manutenções"));
      })
      .finally(() => setLoading(false));
  }, [activeTab, loadScheduledMaintenances, t]);

  const applySelection = async (event) => {
    if (event) event.preventDefault();
    if (!draftCanContinue) {
      toast.warn(t("Escolha ao menos um operador e o período para continuar."));
      return;
    }

    const selectedOperators = operators.filter((item) =>
      draftOperatorIds.includes(Number(item.id))
    );

    let period = resolveDraftPeriod(
      draftPeriodPreset,
      draftDateFrom,
      draftDateTo
    );

    if (draftPeriodPreset === PERIOD_PRESETS.LAST_SERVICE_MONTH) {
      try {
        const baseSelection = {
          operatorIds: draftOperatorIds,
          operatorNames: selectedOperators.map((item) => item.name),
          allMachines: true,
          equipmentIds: [],
          machineIdents: [],
        };
        const summaryData = await fetchFieldAppSummary(farmId, baseSelection);
        period = buildServiceMonthPeriod(
          summaryData.lastSyncAt || summaryData.lastFuelingAt
        );
      } catch (error) {
        console.error(error);
        toast.error(t("Falha ao calcular o último mês de serviço"));
        return;
      }
    }

    const next = {
      operatorIds: draftOperatorIds.map(Number),
      operatorNames: selectedOperators.map((item) => item.name),
      allMachines: true,
      equipmentIds: [],
      machineIdents: [],
      dateFrom: period.dateFrom,
      dateTo: period.dateTo,
      periodAutoToday: false,
      periodPreset: period.periodPreset,
    };

    writeStoredSelection(farmId, next);
    setSelection(next);
    const nextPeriodDayKeys = dayKeysForPeriod(next.dateFrom, next.dateTo);
    setSelectedDayKeys(defaultVisibleDayKeys(nextPeriodDayKeys));
    setSelectedRecord(null);
  };

  const clearSelection = () => {
    setSelection(null);
    setSelectedDayKeys([]);
    setSummary(null);
    setFuelingRows([]);
    setSyncRows([]);
    setScheduleRows([]);
    setInputWorkGroups([]);
    setAlertCount(0);
    setSelectedRecord(null);
    setSelectionStep("operators");
  };

  const openPendingActionInTracking = useCallback(
    async (item) => {
      const actionFarmId = item.farmId ? String(item.farmId) : farmId;
      let nextOperators = operators;
      let nextMachines = machines;
      if (!nextOperators.length || !nextMachines.length) {
        try {
          const data = await fetchFieldAppFilters(actionFarmId, []);
          nextOperators = data.operators || [];
          nextMachines = data.machines || [];
          setOperators(nextOperators);
          setMachines(nextMachines);
        } catch (error) {
          console.error(error);
          toast.error(t("Falha ao preparar o rastreamento"));
          return;
        }
      }

      const dayKey = item.finishedAt ? String(item.finishedAt).slice(0, 10) : "";
      const period = dayKey
        ? buildCustomPeriod(dayKey, dayKey)
        : buildLastWeekPeriod();
      const machine = nextMachines.find(
        (entry) => entry.ident === item.machineIdent
      );

      const next = {
        operatorIds: item.operatorId ? [Number(item.operatorId)] : [],
        operatorNames: item.operatorName ? [item.operatorName] : [],
        allMachines: false,
        equipmentIds: machine ? [Number(machine.id)] : [],
        machineIdents: item.machineIdent ? [item.machineIdent] : [],
        dateFrom: period.dateFrom,
        dateTo: period.dateTo,
        periodAutoToday: false,
        periodPreset: PERIOD_PRESETS.CUSTOM,
      };

      writeStoredSelection(actionFarmId, next);
      setSelection(next);
      setDraftOperatorIds(next.operatorIds);
      setDraftAllMachines(false);
      setDraftEquipmentIds(next.equipmentIds);
      setDraftPeriodPreset(PERIOD_PRESETS.CUSTOM);
      setDraftDateFrom(datePart(period.dateFrom));
      setDraftDateTo(datePart(period.dateTo));
      setSelectedDayKeys(
        dayKey ? [dayKey] : dayKeysForPeriod(next.dateFrom, next.dateTo)
      );
      setFocusSessionUuid(item.sessionUuid || null);
      setActiveTab("tracking");
      setTrackingRefreshToken((value) => value + 1);
    },
    [farmId, machines, operators, t]
  );

  const appliedTrackingDeepLinkRef = useRef("");

  useEffect(() => {
    if (filtersLoading) return;
    if (resolveFieldAppTab(location.search) !== "tracking") return;
    const params = new URLSearchParams(location.search);
    const rangeFrom = params.get("from");
    const rangeTo = params.get("to");
    const machinesParam = params.get("machines") || "";
    if (rangeFrom && rangeTo && (machinesParam || params.get("operators"))) {
      const key = `range|${machinesParam}|${params.get("operators") || ""}|${rangeFrom}|${rangeTo}`;
      if (appliedTrackingDeepLinkRef.current === key) return;

      const wantedIdents = machinesParam
        .split(",")
        .map((ident) => ident.trim())
        .filter(Boolean);
      if (wantedIdents.length && !machines.length) return;
      appliedTrackingDeepLinkRef.current = key;
      const compactIdent = (value) =>
        String(value || "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "");
      const matchedMachines = machines.filter((machine) =>
        wantedIdents.some(
          (ident) => compactIdent(machine.ident) === compactIdent(ident)
        )
      );
      const operatorIds = (params.get("operators") || "")
        .split(",")
        .map((id) => Number(id))
        .filter((id) => id);
      const period = buildCustomPeriod(rangeFrom, rangeTo);
      const next = {
        operatorIds,
        operatorNames: operatorIds
          .map((id) => {
            const found = operators.find((item) => Number(item.id) === id);
            return found ? found.name : "";
          })
          .filter(Boolean),
        allMachines: false,
        equipmentIds: matchedMachines.map((machine) => Number(machine.id)),
        machineIdents: matchedMachines.length
          ? matchedMachines.map((machine) => machine.ident)
          : wantedIdents,
        dateFrom: period.dateFrom,
        dateTo: period.dateTo,
        periodAutoToday: false,
        periodPreset: PERIOD_PRESETS.CUSTOM,
      };
      writeStoredSelection(farmId, next);
      setSelection(next);
      setDraftOperatorIds(next.operatorIds);
      setDraftAllMachines(false);
      setDraftEquipmentIds(next.equipmentIds);
      setDraftPeriodPreset(PERIOD_PRESETS.CUSTOM);
      setDraftDateFrom(datePart(period.dateFrom));
      setDraftDateTo(datePart(period.dateTo));
      setSelectedDayKeys(dayKeysForPeriod(next.dateFrom, next.dateTo));
      setFocusSessionUuid(null);
      setActiveTab("tracking");
      setTrackingViewMode("map");
      setTrackingRefreshToken((value) => value + 1);
      return;
    }

    const operatorId = params.get("operator");
    const day = params.get("day");
    if (!operatorId || !day) return;
    const machineIdent = params.get("machine") || "";
    const sessionUuid = params.get("session") || "";
    const key = `${operatorId}|${machineIdent}|${day}|${sessionUuid}`;
    if (appliedTrackingDeepLinkRef.current === key) return;
    appliedTrackingDeepLinkRef.current = key;

    const operator = operators.find(
      (item) => String(item.id) === String(operatorId)
    );
    openPendingActionInTracking({
      farmId,
      operatorId: Number(operatorId),
      operatorName: operator ? operator.name : "",
      machineIdent,
      finishedAt: day,
      sessionUuid: sessionUuid || null,
    });
  }, [
    farmId,
    filtersLoading,
    location.search,
    machines,
    openPendingActionInTracking,
    operators,
  ]);

  const applyPeriodPreset = useCallback(
    async (preset) => {
      if (!selection) return;

      let period;
      if (preset === PERIOD_PRESETS.LAST_SERVICE_MONTH) {
        try {
          const summaryData = await fetchFieldAppSummary(farmId, {
            ...selection,
            dateFrom: undefined,
            dateTo: undefined,
          });
          period = buildServiceMonthPeriod(
            summaryData.lastSyncAt || summaryData.lastFuelingAt
          );
        } catch (error) {
          console.error(error);
          toast.error(t("Falha ao calcular o último mês de serviço"));
          return;
        }
      } else if (preset === PERIOD_PRESETS.LAST_MONTH) {
        period = buildLastMonthPeriod();
      } else if (preset === PERIOD_PRESETS.MONTH_TO_DATE) {
        period = buildMonthToDatePeriod();
      } else if (preset === PERIOD_PRESETS.CUSTOM) {
        period = buildCustomPeriod(
          datePart(selection.dateFrom) || draftDateFrom,
          datePart(selection.dateTo) || draftDateTo
        );
      } else {
        period = buildLastWeekPeriod();
      }

      const next = {
        ...selection,
        dateFrom: period.dateFrom,
        dateTo: period.dateTo,
        periodPreset: period.periodPreset,
        periodAutoToday: false,
      };

      writeStoredSelection(farmId, next);
      setSelection(next);
      const nextPeriodDayKeys = dayKeysForPeriod(next.dateFrom, next.dateTo);
      setSelectedDayKeys(defaultVisibleDayKeys(nextPeriodDayKeys));
      setDraftPeriodPreset(period.periodPreset);
      setDraftDateFrom(datePart(period.dateFrom));
      setDraftDateTo(datePart(period.dateTo));
    },
    [draftDateFrom, draftDateTo, farmId, selection, t]
  );

  const applyBannerCustomDates = useCallback(
    (fromDay, toDay) => {
      if (!selection || !fromDay || !toDay || fromDay > toDay) return;
      const period = buildCustomPeriod(fromDay, toDay);
      const next = {
        ...selection,
        dateFrom: period.dateFrom,
        dateTo: period.dateTo,
        periodPreset: PERIOD_PRESETS.CUSTOM,
        periodAutoToday: false,
      };
      writeStoredSelection(farmId, next);
      setSelection(next);
      const nextPeriodDayKeys = dayKeysForPeriod(next.dateFrom, next.dateTo);
      setSelectedDayKeys(defaultVisibleDayKeys(nextPeriodDayKeys));
      setDraftPeriodPreset(PERIOD_PRESETS.CUSTOM);
      setDraftDateFrom(fromDay);
      setDraftDateTo(toDay);
    },
    [farmId, selection]
  );

  const handleToggleBannerDay = useCallback(
    (dayKey) => {
      setSelectedDayKeys((current) =>
        applyChartDayClick(current, dayKey, periodDayKeys)
      );
    },
    [periodDayKeys]
  );

  const handleShowAllDays = useCallback(() => {
    setSelectedDayKeys(periodDayKeys.slice());
  }, [periodDayKeys]);

  const handleDraftPresetChange = (preset) => {
    setDraftPeriodPreset(preset);
    if (preset === PERIOD_PRESETS.CUSTOM) {
      return;
    }
    const period = resolveDraftPeriod(preset, draftDateFrom, draftDateTo);
    setDraftDateFrom(datePart(period.dateFrom));
    setDraftDateTo(datePart(period.dateTo));
  };

  const handleTabChange = async (tabId) => {
    setActiveTab(tabId);
    setSelectedRecord(null);
    if (!selectionReady) return;

    if (selection) {
      const refreshed = refreshLivePeriodEnd(selection);
      if (refreshed !== selection) {
        const previousDays = dayKeysForPeriod(selection.dateFrom, selection.dateTo);
        const nextDays = dayKeysForPeriod(refreshed.dateFrom, refreshed.dateTo);
        writeStoredSelection(farmId, refreshed);
        setSelection(refreshed);
        setDraftDateFrom(datePart(refreshed.dateFrom));
        setDraftDateTo(datePart(refreshed.dateTo));
        setDraftPeriodPreset(refreshed.periodPreset || PERIOD_PRESETS.CUSTOM);
        setSelectedDayKeys((current) => {
          if (
            !current ||
            !current.length ||
            allPeriodDaysSelected(previousDays, current)
          ) {
            return defaultVisibleDayKeys(nextDays);
          }
          const kept = current.filter((key) => nextDays.indexOf(key) !== -1);
          return kept.length ? kept : defaultVisibleDayKeys(nextDays);
        });
      }
    }

    if (tabId === "tracking") {
      return;
    }
    setLoading(true);
    try {
      const tab = TABS.find((item) => item.id === tabId) || TABS[0];
      if (tabId === "fueling") {
        await loadFueling();
      } else if (tabId === "maintenance") {
        await loadScheduledMaintenances();
      } else if (tabId === "summary") {
        await loadSummary();
        await loadAlertCount();
      } else if (tabId === "inputs") {
        await loadInputs();
      } else if (tabId !== "operator-alerts") {
        await loadSync(tab);
      }
    } catch (error) {
      console.error(error);
      toast.error(t("Falha ao carregar dados do app de campo"));
    } finally {
      setLoading(false);
    }
  };

  const openRecordDetail = async (record) => {
    setDetailLoading(true);
    setSelectedRecord(null);
    try {
      if (record.uuid && record.record_type !== "fuel_record") {
        const detail = await fetchSyncRecordDetail(farmId, record.uuid);
        setSelectedRecord(detail);
      } else {
        setSelectedRecord(record);
      }
    } catch (error) {
      console.error(error);
      toast.error(t("Falha ao carregar detalhe do registro"));
    } finally {
      setDetailLoading(false);
    }
  };

  const openFuelingDetail = (row) => {
    setSelectedRecord(fuelRecordToDetail(row));
  };

  const toggleAllOperators = () => {
    if (draftOperatorIds.length === operators.length) {
      setDraftOperatorIds([]);
      setDraftAllMachines(true);
      setDraftEquipmentIds([]);
    } else {
      setDraftOperatorIds(operators.map((item) => Number(item.id)));
    }
  };

  const toggleDraftOperator = (operatorId) => {
    setDraftOperatorIds((current) => {
      const next = toggleId(current, operatorId);
      if (!next.length) {
        setDraftAllMachines(true);
        setDraftEquipmentIds([]);
      }
      return next;
    });
  };

  const selectTrackingOperator = (operatorId) => {
    const value = Number(operatorId);
    setTrackingOperatorId(value);
    setTrackingRefreshToken((current) => current + 1);
  };

  const openIndividualTracking = () => {
    setTrackingViewMode("detail");
    if (selection && selection.operatorIds && selection.operatorIds.length) {
      setTrackingOperatorId((current) => {
        if (
          current != null &&
          selection.operatorIds.some((id) => Number(id) === Number(current))
        ) {
          return current;
        }
        return Number(selection.operatorIds[0]);
      });
    }
    setTrackingRefreshToken((current) => current + 1);
  };

  const renderTrackingOperatorChoice = () => {
    if (!selection || selection.operatorIds.length <= 1) {
      return null;
    }

    const operatorIds = selection.operatorIds.map(Number);

    return (
      <div className="field-app-tracking-operator-choice">
        <div className="field-app-tracking-view-toggle" role="group">
          <button
            type="button"
            className={`btn btn-sm${
              trackingViewMode === "map" ? " btn-success" : " btn-default"
            }`}
            onClick={() => setTrackingViewMode("map")}
          >
            {t("Mapa de todos os rastros")}
          </button>
          <button
            type="button"
            className={`btn btn-sm${
              trackingViewMode === "detail" ? " btn-success" : " btn-default"
            }`}
            onClick={openIndividualTracking}
          >
            {t("Detalhe individual")}
          </button>
        </div>
        {trackingViewMode === "detail" ? (
        <div className="alert alert-warning" role="alert">
          {t(
            "Para o gráfico, as horas e os hectares, escolha um operador por vez."
          )}
        </div>
        ) : null}
        {trackingViewMode === "detail" ? (
        <div className="field-app-selection-field">
          <label>
            <FaUser style={{ marginRight: 6 }} />
            {t("Operador no rastreamento")}
          </label>
          <div className="field-app-check-list">
            {operatorIds.map((operatorId) => {
              const operator = operators.find(
                (item) => Number(item.id) === operatorId
              );
              const nameIndex = selection.operatorIds.findIndex(
                (id) => Number(id) === operatorId
              );
              const fallbackName =
                nameIndex >= 0 &&
                selection.operatorNames &&
                selection.operatorNames[nameIndex]
                  ? selection.operatorNames[nameIndex]
                  : String(operatorId);
              const checked = trackingOperatorId === operatorId;
              return (
                <label
                  key={operatorId}
                  className={`field-app-radio-row field-app-check-item ${
                    checked ? "is-checked" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="field-app-tracking-operator"
                    checked={checked}
                    onChange={() => selectTrackingOperator(operatorId)}
                  />
                  <span>
                    {operator ? operator.name : fallbackName}
                    {operator && operator.ident ? (
                      <small> ({operator.ident})</small>
                    ) : null}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
        ) : null}
      </div>
    );
  };

  const goToPeriodStep = (event) => {
    if (event) event.preventDefault();
    if (!draftOperatorsReady) {
      toast.warn(t("Escolha ao menos um operador."));
      return;
    }
    setSelectionStep("period");
  };

  const renderSelectionGate = () => (
    <div className="field-app-selection">
      <div className="field-app-selection-card">
        <ol className="field-app-selection-steps">
          <li
            className={`field-app-selection-step${
              selectionStep === "operators" ? " is-active" : " is-done"
            }`}
          >
            1. {t("Operador")}
          </li>
          <li
            className={`field-app-selection-step${
              selectionStep === "period" ? " is-active" : ""
            }`}
          >
            2. {t("Período")}
          </li>
        </ol>
        <h3>
          {selectionStep === "operators"
            ? t("Quem você quer ver?")
            : t("Qual o período?")}
        </h3>
        <p>
          {selectionStep === "operators"
            ? t("Escolha um ou mais operadores. Depois você informa o período.")
            : t(
                "Agora escolha o período. O rastreamento e as outras abas usam essa mesma escolha."
              )}
        </p>
        {filtersLoading ? (
          <div className="field-app-loading field-app-loading--compact">
            <MDSpinner singleColor="#1ABB9C" size={30} />
          </div>
        ) : (
          <>
            {filtersLoadError ? (
              <div className="alert alert-danger" role="alert">
                {filtersLoadError}
              </div>
            ) : null}
            {selectionStep === "operators" ? (
              <form
                className="field-app-selection-form"
                onSubmit={goToPeriodStep}
              >
                <div className="field-app-selection-field">
                  <div className="field-app-selection-field-head">
                    <label>
                      <FaUser style={{ marginRight: 6 }} />
                      {t("Operador")}
                    </label>
                    {operators.length ? (
                      <button
                        type="button"
                        className="btn btn-link btn-sm field-app-link-btn"
                        onClick={toggleAllOperators}
                      >
                        {draftOperatorIds.length === operators.length
                          ? t("Limpar")
                          : t("Marcar todos")}
                      </button>
                    ) : null}
                  </div>
                  <div className="field-app-check-list">
                    {operators.length === 0 ? (
                      <div className="field-app-check-empty">
                        {t("Nenhum operador cadastrado nesta fazenda.")}{" "}
                        <Link to={`/${farmId}/operator/add`}>
                          {t("Cadastrar operador")}
                        </Link>
                      </div>
                    ) : (
                      operators.map((operator) => {
                        const checked = draftOperatorIds.includes(
                          Number(operator.id)
                        );
                        return (
                          <label
                            key={operator.id}
                            className={`field-app-check-item ${
                              checked ? "is-checked" : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleDraftOperator(operator.id)}
                            />
                            <span>
                              {operator.name}
                              {operator.ident ? (
                                <small> ({operator.ident})</small>
                              ) : null}
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
                <div className="field-app-selection-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!draftOperatorsReady}
                  >
                    {t("Continuar para o período")}
                  </button>
                </div>
              </form>
            ) : (
              <form
                className="field-app-selection-form"
                onSubmit={applySelection}
              >
                <div className="field-app-selection-field">
                  <label>
                    <FaCalendarAlt style={{ marginRight: 6 }} />
                    {t("Período")}
                  </label>
                  <p className="field-app-intro field-app-intro--compact text-muted">
                    {t(
                      "Semana e mês já selecionam todos os dias. O rastreamento das máquinas continua carregando um dia de cada vez para não pesar."
                    )}
                  </p>
                  <div className="field-app-period-presets">
                    {[
                      PERIOD_PRESETS.LAST_WEEK,
                      PERIOD_PRESETS.LAST_MONTH,
                      PERIOD_PRESETS.MONTH_TO_DATE,
                      PERIOD_PRESETS.LAST_SERVICE_MONTH,
                      PERIOD_PRESETS.CUSTOM,
                    ].map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        className={`btn btn-xs field-app-period-preset-btn${
                          draftPeriodPreset === preset ? " is-selected" : ""
                        }`}
                        onClick={() => handleDraftPresetChange(preset)}
                      >
                        {presetLabel(preset, t)}
                      </button>
                    ))}
                  </div>
                  {draftPeriodPreset === PERIOD_PRESETS.CUSTOM ? (
                    <div className="field-app-period-row">
                      <div>
                        <span className="field-app-period-label">{t("De")}</span>
                        <input
                          type="date"
                          className="form-control"
                          value={draftDateFrom}
                          max={draftDateTo || undefined}
                          onChange={(event) =>
                            setDraftDateFrom(event.target.value)
                          }
                        />
                      </div>
                      <div>
                        <span className="field-app-period-label">{t("Até")}</span>
                        <input
                          type="date"
                          className="form-control"
                          value={draftDateTo}
                          min={draftDateFrom || undefined}
                          onChange={(event) => setDraftDateTo(event.target.value)}
                        />
                      </div>
                    </div>
                  ) : (
                    <p className="field-app-period-summary text-muted">
                      {formatDateLabel(draftDateFrom)} —{" "}
                      {formatDateLabel(draftDateTo)}
                    </p>
                  )}
                </div>
                <div className="field-app-selection-actions">
                  <button
                    type="button"
                    className="btn btn-default"
                    onClick={() => setSelectionStep("operators")}
                  >
                    {t("Voltar")}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!draftCanContinue}
                  >
                    {activeTab === "tracking"
                      ? t("Ver rastreamento")
                      : t("Continuar")}
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );

  const renderSummaryCards = () => {
    if (!summary) return null;
    const cards = [
      {
        label: t("Abastecimentos recebidos"),
        value: summary.fuelingRecords || 0,
        hint: formatDateTime(summary.lastFuelingAt),
        tabId: "fueling",
      },
      {
        label: t("Máquinas assumidas"),
        value:
          (summary.syncEventsByType &&
            summary.syncEventsByType.ASSUMIR_MAQUINA) ||
          0,
        hint: t("Assumir máquina no app de campo"),
        tabId: "machines",
      },
      {
        label: t("Registros de sync"),
        value: summary.syncRecordsTotal || 0,
        hint: formatDateTime(summary.lastSyncAt),
        tabId: "all",
      },
      {
        label: t("Alertas ao app"),
        value: alertCount,
        hint: t("Avisos enviados ao tablet do operador"),
        tabId: "operator-alerts",
      },
      {
        label: t("Insumos (trabalhos + paradas)"),
        value:
          ((summary.syncRecordsByType &&
            summary.syncRecordsByType.input_refill_work) ||
            0) +
          ((summary.syncRecordsByType &&
            summary.syncRecordsByType.input_refill_record) ||
            0),
        hint: t("Dosagem por abastecimento"),
        tabId: "inputs",
      },
      {
        label: t("Eventos operacionais"),
        value:
          (summary.syncRecordsByType &&
            summary.syncRecordsByType.operational_event) ||
          0,
        hint: t("Assumir máquina, manutenção, etc."),
        tabId: "events",
      },
    ];

    return (
      <>
        <div className="field-app-summary-grid">
          {cards.map((card) => (
            <button
              type="button"
              key={card.label}
              className="field-app-summary-card field-app-summary-card--link"
              onClick={() => card.tabId && handleTabChange(card.tabId)}
            >
              <div className="field-app-summary-value">{card.value}</div>
              <div className="field-app-summary-label">{card.label}</div>
              <div className="field-app-summary-hint">{card.hint}</div>
            </button>
          ))}
        </div>

        <div className="field-app-mirror">
          <h3>{t("O que o operador vê no app")}</h3>
          <p>
            {t(
              "Use estas abas para acompanhar na web as mesmas funções da tela inicial do app de campo."
            )}
          </p>
          <div className="field-app-mirror-grid">
            {APP_MIRROR_SECTIONS.map((section) => (
              <button
                type="button"
                key={section.tabId}
                className="field-app-mirror-card"
                onClick={() => handleTabChange(section.tabId)}
              >
                <strong>{t(section.title)}</strong>
                <span>{t(section.text)}</span>
              </button>
            ))}
          </div>
        </div>
      </>
    );
  };

  const renderFuelingTable = () => {
    const showingFuelExample = enrichedFueling.rows.length === 0;

    const initialOperatorId =
      selection &&
      !selection.allOperators &&
      selection.operatorIds &&
      selection.operatorIds.length === 1
        ? String(selection.operatorIds[0])
        : "";

    const initialMachineIdent =
      selection &&
      !selection.allMachines &&
      selection.machineIdents &&
      selection.machineIdents.length === 1
        ? selection.machineIdents[0]
        : "";

    return (
      <FieldAppFuelControlPanel
        t={t}
        summary={enrichedFueling.summary}
        machineSummaries={fuelMachineSummaries}
        recordCount={fuelingRows.length}
      >
        <FieldAppFuelSupplySpreadsheet
          key={`${initialOperatorId}-${initialMachineIdent}-${selectedPeriodLabel}`}
          rows={enrichedFueling.rows}
          showingExample={showingFuelExample}
          exampleRows={FUEL_CONTROL_EXAMPLE_RECORDS}
          formatDateTime={formatDateTime}
          t={t}
          onOpenDetail={openFuelingDetail}
          periodLabel={selectedPeriodLabel}
          operatorLabel={selectedOperatorLabel}
          machineLabel={selectedMachineLabel}
          initialOperatorId={initialOperatorId}
          initialMachineIdent={initialMachineIdent}
          farmId={farmId}
          baseSelection={effectiveSelection}
        />
      </FieldAppFuelControlPanel>
    );
  };

  const maintenanceOverdueCount = useMemo(
    () =>
      countOverdueMaintenances({
        results: scheduleRows,
        fieldRecords: maintenanceFieldRecords,
      }),
    [scheduleRows, maintenanceFieldRecords]
  );

  const sortedScheduleRows = useMemo(
    () => [...scheduleRows].sort(compareMaintenanceProximity),
    [scheduleRows]
  );

  const scheduleCountByEquipment = useMemo(() => {
    const counts = {};
    scheduleRows.forEach((row) => {
      const key = equipmentKeyFromScheduleRow(row);
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [scheduleRows]);

  const machineMaintenancesRows = useMemo(() => {
    if (!machineMaintenancesKey) return null;
    return scheduleRows.filter(
      (row) => equipmentKeyFromScheduleRow(row) === machineMaintenancesKey
    );
  }, [machineMaintenancesKey, scheduleRows]);

  const machineMaintenancesName = useMemo(() => {
    if (!machineMaintenancesRows || !machineMaintenancesRows.length) return "";
    return machineMaintenancesRows[0].equipment_name || "";
  }, [machineMaintenancesRows]);

  const maintenanceMachineOptions = useMemo(() => {
    const byId = new Map();
    scheduleRows.forEach((row) => {
      const id = row.equipmentId != null ? row.equipmentId : row.equipment_id;
      if (id == null || byId.has(String(id))) return;
      byId.set(String(id), {
        equipmentId: id,
        equipmentName: row.equipment_name || String(id),
        meterType: row.meterType || row.meter_type,
      });
    });
    return Array.from(byId.values()).sort((a, b) =>
      String(a.equipmentName).localeCompare(String(b.equipmentName), "pt-BR")
    );
  }, [scheduleRows]);

  const renderScheduledTable = () => (
    <>
      {maintenanceOverdueCount > 0 ? (
        <div
          className="field-app-maintenance-alert-banner"
          role="alert"
        >
          <FaExclamationTriangle aria-hidden className="field-app-maintenance-alert-banner-icon" />
          <div>
            <strong>
              {maintenanceOverdueCount === 1
                ? t("1 manutenção vencida nesta fazenda")
                : t("{{count}} manutenções vencidas nesta fazenda", {
                    count: maintenanceOverdueCount,
                  })}
            </strong>
            <p>
              {t(
                "O horímetro atual da máquina atingiu ou ultrapassou o valor programado. As vencidas aparecem em vermelho. Use Concluir manutenção hoje e informe o horímetro ou a quilometragem da máquina. Esta lista mostra todas as máquinas da fazenda, sem precisar filtrar operador."
              )}
            </p>
            <Link
              className="btn btn-sm btn-danger field-app-maintenance-alert-banner-link"
              to={`/${farmId}/field-app/?tab=pending-actions&alert=maintenance_overdue`}
            >
              {t("Ver em Ações pendentes")}
            </Link>
          </div>
        </div>
      ) : null}

      <p className="text-muted field-app-maintenance-field-records-hint">
        {t(
          "Lista ordenada pelas manutenções mais próximas do vencimento. “Faltam” é calculado (alvo − última leitura) e não se edita. Clique na máquina para ver e editar todas as revisões dela. Use Adicionar manutenção para criar outra revisão no mesmo veículo."
        )}
      </p>
      <div className="field-app-maintenance-toolbar">
        <button
          type="button"
          className="btn btn-sm btn-success"
          onClick={() => openAddMaintenance(null)}
          disabled={!maintenanceMachineOptions.length}
        >
          {t("Adicionar manutenção")}
        </button>
      </div>
      <div className="table-responsive">
        <table className="table table-striped jambo_table field-app-maintenance-table">
          <thead>
            <tr>
              <th>{t("Máquina")}</th>
              <th>{t("Tarefa")}</th>
              <th>{t("Status")}</th>
              <th title={t("Alvo programado menos a última leitura conhecida — não editável")}>
                {t("Faltam")}
              </th>
              <th title={t("Horímetro ou hodômetro em que a próxima manutenção deve ser feita")}>
                {t("Próxima manutenção com")}
              </th>
              <th title={t("Última leitura conhecida da máquina (app ou telemetria)")}>
                {t("Leitura atual")}
              </th>
              <th>{t("Origem")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortedScheduleRows.length === 0 ? (
              <tr>
                <td colSpan="8">
                  {t(
                    "Nenhuma manutenção agendada. Registre no app de campo."
                  )}
                </td>
              </tr>
            ) : (
              sortedScheduleRows.map((row) => {
                const remaining = computeMaintenanceRemaining(row);
                const equipmentKey = equipmentKeyFromScheduleRow(row);
                const totalForMachine = scheduleCountByEquipment[equipmentKey] || 1;
                return (
                <tr
                  key={row.id}
                  className={
                    row.isOverdue
                      ? "field-app-maintenance-row-overdue field-app-maintenance-row-overdue--scheduled"
                      : undefined
                  }
                >
                  <td>
                    <button
                      type="button"
                      className="field-app-maintenance-machine-link"
                      onClick={() => setMachineMaintenancesKey(equipmentKey)}
                      title={t("Ver todas as manutenções desta máquina")}
                    >
                      {row.isOverdue ? (
                        <span className="field-app-maintenance-machine-overdue">
                          <FaExclamationTriangle aria-hidden />
                          {row.equipment_name || "—"}
                        </span>
                      ) : (
                        row.equipment_name || "—"
                      )}
                      {totalForMachine > 1 ? (
                        <span className="field-app-maintenance-machine-count">
                          {t("{{count}} revisões", { count: totalForMachine })}
                        </span>
                      ) : null}
                    </button>
                  </td>
                  <td>{row.name}</td>
                  <td>
                    {row.isOverdue ? (
                      <span className="field-app-maintenance-status-overdue">
                        {t("Vencida")}
                        {row.hoursOverdue != null
                          ? ` (+${formatHourMeter(row.hoursOverdue)} h)`
                          : null}
                      </span>
                    ) : (
                      row.status_display || row.status
                    )}
                  </td>
                  <td
                    className={
                      remaining.remaining != null && remaining.remaining < 0
                        ? "field-app-maintenance-current-overdue"
                        : undefined
                    }
                    title={t("Calculado automaticamente — não editável")}
                  >
                    {formatMaintenanceRemaining(remaining, t)}
                  </td>
                  <td>
                    <FieldAppMaintenanceEditableHours
                      value={row.targetHourMeterDisplay}
                      field="targetHourMeter"
                      maintenanceId={row.id}
                      t={t}
                      onSave={handleScheduledMaintenanceSave}
                      overdue={row.isOverdue}
                    />
                  </td>
                  <td
                    className={
                      row.isOverdue ? "field-app-maintenance-current-overdue" : undefined
                    }
                  >
                    {formatHourMeter(row.currentHourMeterDisplay)}
                    {remaining.unitLabel ? ` ${remaining.unitLabel}` : ""}
                  </td>
                  <td>
                    {row.fromFieldApp ? t("App de campo") : t("Plataforma")}
                  </td>
                  <td>
                    <div className="field-app-maintenance-row-actions">
                      <button
                        type="button"
                        className="btn btn-sm btn-default"
                        onClick={() =>
                          openAddMaintenance(
                            row.equipmentId != null
                              ? row.equipmentId
                              : row.equipment_id
                          )
                        }
                        title={t("Adicionar outra manutenção neste veículo")}
                      >
                        {t("Nova")}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-success"
                        onClick={() => setCompleteMaintenanceRow(row)}
                      >
                        {t("Concluir manutenção hoje")}
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {maintenanceFieldRecords.length ? (
        <div className="field-app-maintenance-field-records">
          <h4>{t("Registros recentes do app de campo")}</h4>
          <p className="text-muted field-app-maintenance-field-records-hint">
            {t(
              "Manutenções informadas pelos operadores no tablet. Clique em Próxima manutenção com para corrigir o horímetro-alvo. O agendamento acima é atualizado junto. Valores vencidos aparecem em vermelho e geram alerta em Ações pendentes."
            )}
          </p>
          <div className="table-responsive">
            <table className="table table-striped jambo_table field-app-maintenance-table">
              <thead>
                <tr>
                  <th>{t("Recebido em")}</th>
                  <th>{t("Operador")}</th>
                  <th>{t("Máquina")}</th>
                  <th>{t("Tarefa")}</th>
                  <th title={t("Horímetro informado pelo operador no momento do registro")}>
                    {t("Horímetro ao registrar (h)")}
                  </th>
                  <th title={t("Alvo programado menos a última leitura — não editável")}>
                    {t("Faltam")}
                  </th>
                  <th title={t("Horímetro em que a próxima manutenção deve ser feita")}>
                    {t("Próxima manutenção com")}
                  </th>
                  <th title={t("Último horímetro conhecido da máquina")}>
                    {t("Leitura atual")}
                  </th>
                  <th>{t("Situação")}</th>
                </tr>
              </thead>
              <tbody>
                {maintenanceFieldRecords.map((row) => {
                  const remaining = computeMaintenanceRemaining(row);
                  return (
                  <tr
                    key={row.uuid}
                    className={
                      row.isOverdue
                        ? "field-app-maintenance-row-overdue field-app-maintenance-row-overdue--field"
                        : undefined
                    }
                  >
                    <td>{formatDateTime(row.receivedAt)}</td>
                    <td>{row.operatorName || "—"}</td>
                    <td>{row.machineIdent || "—"}</td>
                    <td>{row.maintenanceType || "—"}</td>
                    <td>{formatHourMeter(row.hourMeter)}</td>
                    <td
                      className={
                        remaining.remaining != null && remaining.remaining < 0
                          ? "field-app-maintenance-current-overdue"
                          : undefined
                      }
                      title={t("Calculado automaticamente — não editável")}
                    >
                      {formatMaintenanceRemaining(remaining, t)}
                    </td>
                    <td>
                      <FieldAppMaintenanceEditableHours
                        value={
                          row.targetHourMeterDisplay != null
                            ? row.targetHourMeterDisplay
                            : row.targetHourMeter
                        }
                        field="targetHourMeter"
                        recordUuid={row.uuid}
                        maintenanceId={row.scheduledMaintenanceId}
                        t={t}
                        onSave={handleMaintenanceFieldRecordSave}
                        overdue={row.isOverdue}
                      />
                    </td>
                    <td
                      className={
                        row.isOverdue ? "field-app-maintenance-current-overdue" : undefined
                      }
                    >
                      {formatHourMeter(row.currentHourMeterDisplay)}
                    </td>
                    <td>
                      {row.isOverdue ? (
                        <span className="field-app-maintenance-status-overdue">
                          {t("Vencida")}
                          {row.hoursOverdue != null
                            ? ` (+${formatHourMeter(row.hoursOverdue)} h)`
                            : null}
                        </span>
                      ) : (
                        <span className="field-app-maintenance-status-ok">{t("Em dia")}</span>
                      )}
                      {row.scheduledMaintenanceId ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-success"
                          style={{ marginLeft: 8 }}
                          onClick={() => setCompleteMaintenanceRow(row)}
                        >
                          {t("Concluir manutenção hoje")}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  );

  const renderSyncTable = () => (
    <div className="table-responsive">
      <table className="table table-striped jambo_table">
        <thead>
          <tr>
            <th>{t("Recebido em")}</th>
            <th>{t("Tipo")}</th>
            <th>{t("Evento")}</th>
            <th>{t("Operador")}</th>
            <th>{t("Resumo")}</th>
            <th>{t("Foto")}</th>
            <th>{t("Lote")}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {syncRows.length === 0 ? (
            <tr>
              <td colSpan="8">{t("Nenhum registro encontrado.")}</td>
            </tr>
          ) : (
            syncRows.map((row) => (
              <tr key={row.uuid}>
                <td>{formatDateTime(row.received_at)}</td>
                <td>{recordTypeLabel(row.record_type)}</td>
                <td>{eventLabel(row.event_type)}</td>
                <td>{row.operatorName}</td>
                <td>{payloadPreview(row.payloadData)}</td>
                <td>
                  {row.hasPanelPhoto || row.panelPhotoUrl ? (
                    <span
                      className="field-app-fuel-photo-badge"
                      title={t("Foto do painel disponível")}
                    >
                      📷 {t("Sim")}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td>{row.batch_id || "—"}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-default"
                    onClick={() => openRecordDetail(row)}
                  >
                    {t("Detalhe")}
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="x_content field-app-home">
      <Title title={t("App de Campo")} farm_name="" add_button="false" />
      <p className="field-app-intro">
        {t(
          "Dados enviados pelo aplicativo de campo. As telas antigas da plataforma continuam disponíveis — esta área concentra o que chega pelo app novo."
        )}
      </p>

      <ul className="nav nav-tabs bar_tabs field-app-tabs" role="tablist">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <li
              key={tab.id}
              className={`nav-item ${activeTab === tab.id ? "active" : ""}`}
            >
              <button
                type="button"
                className="nav-link field-app-tab-button"
                onClick={() => handleTabChange(tab.id)}
              >
                <Icon className="field-app-tab-icon" />
                {t(tab.label)}
              </button>
            </li>
          );
        })}
      </ul>

      {selectionReady && !TABS_WITHOUT_SELECTION.has(activeTab) ? (
        <>
          <div className="field-app-selection-banner">
            <div className="field-app-selection-banner-text">
              <div>
                <strong>{t("Operadores")}:</strong> {selectedOperatorLabel}
              </div>
              <div>
                <strong>{t("Máquinas")}:</strong> {selectedMachineLabel}
              </div>
              <div>
                <FaCalendarAlt style={{ marginRight: 4 }} />
                <strong>{t("Período")}:</strong> {selectedPeriodLabel}
              </div>
            </div>
            <button
              type="button"
              className="btn btn-default btn-sm"
              onClick={clearSelection}
            >
              {t("Alterar seleção")}
            </button>
          </div>

          {periodDayButtons.length ? (
            <div className="field-app-graph-day-filters field-app-graph-day-filters--banner">
              <div className="field-app-graph-day-filters-toolbar">
                <span className="field-app-graph-day-filters-label">
                  {t("Dias")}:
                </span>
                <button
                  type="button"
                  className={`btn btn-xs field-app-graph-day-btn field-app-graph-day-btn--all${
                    allBannerDaysSelected ? " is-selected" : ""
                  }`}
                  onClick={handleShowAllDays}
                >
                  {selection &&
                  selection.periodPreset === PERIOD_PRESETS.LAST_WEEK
                    ? t("Semana inteira")
                    : t("Todos os dias")}
                </button>
                <span className="field-app-graph-day-filters-hint field-app-graph-day-filters-hint--compact">
                  {t(
                    "Todos os dias do período já vêm selecionados. Desmarque para filtrar."
                  )}
                </span>
              </div>
              <div className="field-app-graph-day-filters-days">
                {periodDayButtons.map((day) => (
                  <button
                    key={day.key}
                    type="button"
                    className={`btn btn-xs field-app-graph-day-btn${
                      selectedDayKeys.indexOf(day.key) !== -1
                        ? " is-selected"
                        : ""
                    }`}
                    title={day.label}
                    onClick={() => handleToggleBannerDay(day.key)}
                  >
                    {day.shortLabel}
                  </button>
                ))}
              </div>
              <div className="field-app-period-presets field-app-period-presets--banner">
                {[
                  PERIOD_PRESETS.LAST_WEEK,
                  PERIOD_PRESETS.LAST_MONTH,
                  PERIOD_PRESETS.MONTH_TO_DATE,
                  PERIOD_PRESETS.LAST_SERVICE_MONTH,
                  PERIOD_PRESETS.CUSTOM,
                ].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    className={`btn btn-xs field-app-period-preset-btn${
                      selection && selection.periodPreset === preset
                        ? " is-selected"
                        : ""
                    }`}
                    onClick={() => applyPeriodPreset(preset)}
                  >
                    {presetLabel(preset, t)}
                  </button>
                ))}
              </div>
              {selection &&
              selection.periodPreset === PERIOD_PRESETS.CUSTOM ? (
                <div className="field-app-period-row field-app-period-row--banner">
                  <div>
                    <span className="field-app-period-label">{t("De")}</span>
                    <input
                      type="date"
                      className="form-control"
                      value={draftDateFrom}
                      max={draftDateTo || undefined}
                      aria-label={t("Data inicial do período")}
                      onChange={(event) => {
                        const value = event.target.value;
                        setDraftDateFrom(value);
                        applyBannerCustomDates(value, draftDateTo);
                      }}
                    />
                  </div>
                  <div>
                    <span className="field-app-period-label">{t("Até")}</span>
                    <input
                      type="date"
                      className="form-control"
                      value={draftDateTo}
                      min={draftDateFrom || undefined}
                      aria-label={t("Data final do período")}
                      onChange={(event) => {
                        const value = event.target.value;
                        setDraftDateTo(value);
                        applyBannerCustomDates(draftDateFrom, value);
                      }}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}

      {activeTab === "pending-actions" ? (
        <FieldAppPendingActionsPanel
          farmId={farmId}
          t={t}
          onOpenInTracking={openPendingActionInTracking}
        />
      ) : requiresSelection ? (
        renderSelectionGate()
      ) : (
        <>
          <div className="field-app-toolbar">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleRefresh}
            >
              {t("Atualizar")}
            </button>
          </div>

          {loading ? (
            <div className="field-app-loading">
              <MDSpinner singleColor="#1ABB9C" size={40} />
            </div>
          ) : (
            <>
              {activeTab === "summary" && renderSummaryCards()}
              {activeTab === "tracking" && (
                <>
                  {selection &&
                  selection.operatorIds &&
                  selection.operatorIds.length > 1
                    ? renderTrackingOperatorChoice()
                    : null}
                  {trackingRequiresOperatorChoice ? (
                    <div className="field-app-tracking-empty">
                      <p className="text-muted">
                        {t(
                          "Selecione o operador acima para carregar o mapa e o gráfico de rastreamento."
                        )}
                      </p>
                    </div>
                  ) : trackingSelection ? (
                    <FieldAppTrackingPanel
                      farmId={farmId}
                      selection={trackingSelection}
                      chartSelection={trackingChartSelection}
                      combineTrails={showCombinedTrails}
                      selectedDayKeys={selectedDayKeys}
                      onSelectedDayKeysChange={setSelectedDayKeys}
                      periodDayKeys={periodDayKeys}
                      machines={machines}
                      active
                      refreshToken={trackingRefreshToken}
                      focusSessionUuid={focusSessionUuid}
                      onFocusSessionHandled={() => setFocusSessionUuid(null)}
                      focusGraphPeriod={focusGraphPeriod}
                      onFocusGraphPeriodHandled={() => setFocusGraphPeriod(null)}
                      t={t}
                    />
                  ) : null}
                </>
              )}
              {activeTab === "operator-alerts" && (
                <FieldAppOperatorAlertsPanel farmId={farmId} t={t} />
              )}
              {activeTab === "fueling" && renderFuelingTable()}
              {activeTab === "machines" && (
                <FieldAppMachinesPanel
                  farmId={farmId}
                  rows={syncRows}
                  machines={machines}
                  formatDateTime={formatDateTime}
                  onOpenDetail={openRecordDetail}
                  t={t}
                />
              )}
              {activeTab === "maintenance" && (
                <FieldAppMaintenancePanel t={t} farmId={farmId}>
                  {renderScheduledTable()}
                </FieldAppMaintenancePanel>
              )}
              {activeTab === "inputs" && (
                <FieldAppInputsPanel
                  groups={inputWorkGroups}
                  loading={loading}
                  t={t}
                />
              )}
              {activeTab === "telemetry" && (
                <FieldAppTelemetrySchedulePanel
                  rows={telemetryScheduleRows}
                  loading={loading || telemetryScheduleLoading}
                  timezoneMode={readTrackingTimezoneMode(farmId)}
                  farmId={farmId}
                  selection={selection}
                  machines={machines}
                  operatorLabel={selectedOperatorFullLabel}
                  periodLabel={selectedPeriodLabel}
                  machineLabel={selectedMachineLabel}
                  t={t}
                />
              )}
              {activeTab === "calibration" && (
                <FieldAppCalibrationPanel t={t}>
                  <FieldAppCalibrationWorkSection
                    farmId={farmId}
                    t={t}
                    onUnpublishedStatusChange={handleCalibrationUnpublishedStatus}
                    onRegisterPublishHandler={(handler) => {
                      calibrationPublishRef.current = handler;
                    }}
                  />
                  {renderSyncTable()}
                </FieldAppCalibrationPanel>
              )}
              {activeTab === "points-of-interest" && (
                <FieldAppPointsOfInterestPanel
                  farmId={farmId}
                  rows={syncRows}
                  operators={operators}
                  formatDateTime={formatDateTime}
                  onOpenDetail={openRecordDetail}
                  t={t}
                />
              )}
              {activeTab !== "summary" &&
                activeTab !== "tracking" &&
                activeTab !== "operator-alerts" &&
                activeTab !== "fueling" &&
                activeTab !== "machines" &&
                activeTab !== "maintenance" &&
                activeTab !== "inputs" &&
                activeTab !== "telemetry" &&
                activeTab !== "calibration" &&
                activeTab !== "points-of-interest" &&
                renderSyncTable()}
            </>
          )}
        </>
      )}

      {completeMaintenanceRow ? (
        <FieldAppCompleteMaintenanceModal
          row={completeMaintenanceRow}
          t={t}
          saving={completingMaintenance}
          onClose={() =>
            completingMaintenance ? null : setCompleteMaintenanceRow(null)
          }
          onSubmit={handleCompleteMaintenance}
        />
      ) : null}

      {machineMaintenancesRows ? (
        <FieldAppMachineMaintenancesModal
          machineName={machineMaintenancesName}
          rows={machineMaintenancesRows}
          t={t}
          onClose={() => setMachineMaintenancesKey(null)}
          onSave={handleScheduledMaintenanceSave}
          onComplete={(row) => {
            setMachineMaintenancesKey(null);
            setCompleteMaintenanceRow(row);
          }}
          onAdd={() => {
            const first = machineMaintenancesRows[0];
            const equipmentId =
              first &&
              (first.equipmentId != null
                ? first.equipmentId
                : first.equipment_id);
            setMachineMaintenancesKey(null);
            openAddMaintenance(equipmentId);
          }}
        />
      ) : null}

      {addMaintenanceOpen ? (
        <FieldAppAddMaintenanceModal
          farmId={farmId}
          machines={maintenanceMachineOptions}
          initialEquipmentId={addMaintenanceEquipmentId}
          t={t}
          saving={addingMaintenance}
          onClose={() => {
            if (addingMaintenance) return;
            setAddMaintenanceOpen(false);
            setAddMaintenanceEquipmentId(null);
          }}
          onSubmit={handleAddMaintenance}
        />
      ) : null}

      {(selectedRecord || detailLoading) && (
        <div
          className="field-app-modal-backdrop"
          onClick={() => setSelectedRecord(null)}
        >
          <div
            className="field-app-modal"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="field-app-modal-header">
              <h3>{t("Detalhe do registro")}</h3>
              <button
                type="button"
                className="close"
                onClick={() => setSelectedRecord(null)}
                aria-label={t("Fechar")}
              >
                ×
              </button>
            </div>
            {detailLoading ? (
              <div className="field-app-loading field-app-loading--compact">
                <MDSpinner singleColor="#1ABB9C" size={30} />
              </div>
            ) : (
              <FieldAppRecordDetail record={selectedRecord} />
            )}
          </div>
        </div>
      )}

      <FarmMapSyncLeaveModal
        open={calibrationPublishGuard.leavePromptOpen}
        t={t}
        loading={calibrationPublishGuard.leaveActionLoading}
        onStay={calibrationPublishGuard.stayOnPage}
        onLeaveWithoutSync={calibrationPublishGuard.leaveWithoutSync}
        onSyncAndLeave={calibrationPublishGuard.syncAndLeave}
        title={t("Sincronizar ordens com o app de campo?")}
        body={t(
          "Há alterações nas ordens de serviço que ainda não foram enviadas ao app. " +
            "Sem sincronizar, os operadores continuarão vendo a receita anterior. " +
            "As versões já enviadas permanecem no histórico desta página."
        )}
        question={t("Deseja sair sem sincronizar com o app de campo?")}
      />

      <ToastContainer position="bottom-right" />
    </div>
  );
}

export default withTranslation()(FieldAppHome);
